import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { TestTube, Heart, Stethoscope, Baby, Building2, Home, Microscope } from 'lucide-react';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

const QuickServices = () => {
  const { t } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  const services = [
    { id: 1, icon: TestTube, nameKey: 'home.services.bloodTests', route: '/services/blood-tests' },
    { id: 2, icon: Heart, nameKey: 'home.services.hormonePanels', route: '/services/hormone-panels' },
    { id: 3, icon: Stethoscope, nameKey: 'home.services.allergyTests', route: '/services/allergy-tests' },
    { id: 4, icon: Microscope, nameKey: 'home.services.diabeticProfiles', route: '/services/diabetic-profiles' },
    { id: 5, icon: Baby, nameKey: 'home.services.childrenLab', route: '/services/children-lab' },
    { id: 6, icon: Heart, nameKey: 'home.services.womensHealth', route: '/services/womens-health' },
    { id: 7, icon: Building2, nameKey: 'home.services.corporateSolutions', route: '/services/corporate-solutions' },
    { id: 8, icon: Home, nameKey: 'home.services.homeVisits', route: '/services/home-visits' },
  ];

  useEffect(() => {
    const section = sectionRef.current;
    const cards = cardsRef.current;

    if (!section || !cards) return;

    // Animate cards on scroll
    gsap.fromTo(
      cards.children,
      {
        opacity: 0,
        y: 50,
        scale: 0.9,
      },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
        },
      }
    );
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-light-text dark:text-dark-text">
            {t('home.services.title')}
          </h2>
          <p className="text-lg text-light-text-secondary dark:text-dark-text-secondary">
            {t('home.services.subtitle')}
          </p>
        </div>

        <div
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <Link
                key={service.id}
                to={service.route}
                className="group relative p-6 bg-light-secondary dark:bg-dark-secondary rounded-xl hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 mb-4 rounded-full bg-brand-purple-base/10 dark:bg-brand-orange-base/10 flex items-center justify-center group-hover:bg-brand-purple-base/20 dark:group-hover:bg-brand-orange-base/20 transition-colors">
                    <Icon
                      size={32}
                      className="text-brand-purple-base dark:text-brand-orange-base"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-light-text dark:text-dark-text">
                    {t(service.nameKey)}
                  </h3>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default QuickServices;

